<script>
  import Textfield from '@smui/textfield';
  import HelperText from '@smui/textfield/helper-text';
  import Radio from '@smui/radio';
  import FormField from '@smui/form-field';
  import { onMount } from 'svelte';
  import * as yup from 'yup';
  import { billingAddress as schema } from './schemas.js';
  
  let { valid = $bindable(), fields = $bindable(), attemptCount = 0 } = $props();

  const b2bfields = ['company_name', 'tax_id'];

  let errors = $state({
    b2b: null,
    tax_id: null,
    first_name: null,
    last_name: null,
    company_name: null,
    street: null,
    building: null,
    apartment: null,
    city: null,
    zipcode: null,
    country: null,
  });

  let b2bInitialized = false;
  let touched = $state(new Set());

  $effect(() => {
    valid = Object.keys(errors).filter(key => errors[key]).length === 0;
  });

  const show = (f) => errors[f] && (touched.has(f) || attemptCount > 0);
  const handleBlur = (f) => { touched = new Set([...touched, f]); validateField(f); };
  const handleInput = (f) => { validateField(f); };

  $effect(() => {
    if (b2bInitialized && fields.b2b !== undefined) {
      if (fields.b2b) {
        validateField('company_name');
        validateField('tax_id');
        errors = { ...errors, first_name: null, last_name: null };
      } else {
        fields.company_name = '';
        fields.tax_id = '';
        validateField('first_name');
        validateField('last_name');
        errors = { ...errors, company_name: null, tax_id: null };
      }
    }
  });

  function validateField(fieldName) {
    try {
      schema.validateSyncAt(fieldName, fields);
      errors = { ...errors, [fieldName]: null };
    } catch (err) {
      errors = { ...errors, [fieldName]: err.message };
    }
  }

  onMount(() => {
    Object.keys(fields).filter(fieldName => !b2bfields.includes(fieldName)).forEach(fieldName => validateField(fieldName));
    b2bInitialized = true;
  });
</script>

<form>
  <div class="row row-1">
    <div style="margin-bottom: 10px;">
      <FormField>
        <Radio bind:group={fields.b2b} value={false} />
        {#snippet label()}Osoba prywatna{/snippet}
      </FormField>
      <FormField>
        <Radio bind:group={fields.b2b} value={true} />
        {#snippet label()}Firma{/snippet}
      </FormField>
    </div>
  </div>

  {#if fields.b2b}
    <div class="row">
      {#key attemptCount}
        <div class="cell" class:field-shaking={attemptCount > 0 && errors.company_name}>
          <Textfield bind:value={fields.company_name}
            variant="outlined"
            label="Nazwa firmy" 
            invalid={show('company_name')}
            input$autocomplete="organization"
            onblur={() => handleBlur('company_name')}
            oninput={() => handleInput('company_name')}>
            {#snippet helper()}<HelperText persistent>{show('company_name') ? errors.company_name : ''}</HelperText>{/snippet}
          </Textfield>
        </div>
      {/key}

      {#key attemptCount}
        <div class="cell" class:field-shaking={attemptCount > 0 && errors.tax_id}>
          <Textfield bind:value={fields.tax_id}
            variant="outlined"
            label="NIP"
            invalid={show('tax_id')}
            onblur={() => handleBlur('tax_id')}
            oninput={() => handleInput('tax_id')}>
            {#snippet helper()}<HelperText persistent>{show('tax_id') ? errors.tax_id : ''}</HelperText>{/snippet}
          </Textfield>
        </div>
      {/key}
    </div>
  {/if}

  <div class="row row-m1">
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.first_name}>
        <Textfield bind:value={fields.first_name}
          variant="outlined"
          label="Imię"
          invalid={show('first_name')}
          input$autocomplete="given-name"
          onblur={() => handleBlur('first_name')}
          oninput={() => handleInput('first_name')}>
          {#snippet helper()}<HelperText persistent>{show('first_name') ? errors.first_name : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.last_name}>
        <Textfield bind:value={fields.last_name}
          variant="outlined"
          label="Nazwisko"
          name="last_name"
          input$autocomplete="family-name"
          invalid={show('last_name')}
          onblur={() => handleBlur('last_name')}
          oninput={() => handleInput('last_name')}>
          {#snippet helper()}<HelperText persistent>{show('last_name') ? errors.last_name : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
  </div>

  <div class="row row-5 row-m3">
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.street}>
        <Textfield bind:value={fields.street}
          variant="outlined"
          label="Ulica"
          invalid={show('street')}
          input$autocomplete="address-line1"
          onblur={() => handleBlur('street')}
          oninput={() => handleInput('street')}>
          {#snippet helper()}<HelperText persistent invalid>{show('street') ? errors.street : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.building}>
        <Textfield bind:value={fields.building}
          variant="outlined"
          label="Budynek"
          invalid={show('building')}
          onblur={() => handleBlur('building')}
          oninput={() => handleInput('building')}>
          {#snippet helper()}<HelperText persistent invalid>{show('building') ? errors.building : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.apartment}>
        <Textfield bind:value={fields.apartment}
          variant="outlined"
          label="Lokal"
          invalid={show('apartment')}
          onblur={() => handleBlur('apartment')}
          oninput={() => handleInput('apartment')}>
          {#snippet helper()}<HelperText persistent invalid>{show('apartment') ? errors.apartment : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
  </div>

  <div class="row row-3 row-m2">
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.city}>
        <Textfield bind:value={fields.city}
          variant="outlined"
          label="Miejscowość"
          invalid={show('city')}
          input$autocomplete="address-line2"
          onblur={() => handleBlur('city')}
          oninput={() => handleInput('city')}>
          {#snippet helper()}<HelperText persistent invalid>{show('city') ? errors.city : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
    {#key attemptCount}
      <div class="cell" class:field-shaking={attemptCount > 0 && errors.zipcode}>
        <Textfield bind:value={fields.zipcode}
          variant="outlined"
          label="Kod pocztowy"
          invalid={show('zipcode')}
          input$autocomplete="postal-code"
          onblur={() => handleBlur('zipcode')}
          oninput={() => handleInput('zipcode')}>
          {#snippet helper()}<HelperText persistent invalid>{show('zipcode') ? errors.zipcode : ''}</HelperText>{/snippet}
        </Textfield>
      </div>
    {/key}
  </div>


</form>

<style>
  .field-shaking {
    animation: field-shake 0.35s ease;
  }

  @keyframes field-shake {
    0%, 100% { transform: translateX(0); }
    25%       { transform: translateX(-5px); }
    75%       { transform: translateX(5px); }
  }
</style>
